/**
 * Query language — compile AST → analytics FilterExpr (the REFINEMENT layer).
 *
 * EXPERIMENTAL (lewis/query-language-spike). Turns a parsed query AST (see
 * queryLang.js) into a `FilterExpr` in the same shape buildPromptRootAndFilter
 * emits, then `composeFilters` ANDs it onto the sidebar's scope-derived filter.
 * Final spec filter = scopeFilter AND queryFilter — the sidebar keeps driving
 * navigation; the query box only refines.
 *
 * The catalog (QUERY_FIELDS) carries each field's analytics FieldRef
 * (step + field), so non-synthetic clauses compile here directly. Synthetic
 * dims (skill/mcp_server/memory) reuse the existing dim adapter registry
 * via an injectable resolver (defaults to getDimAdapter); cluster/identity are
 * DEFERRED (need cross-entity joins) and reported, not silently dropped.
 *
 * Compile contract:
 *   - AND drops an uncompilable conjunct (broadens — warned). OR is STRICT:
 *     an OR with an uncompilable branch fails the whole OR (dropping a branch
 *     would silently hide rows the user asked for).
 *   - Returns { filter, errors, warnings, deferred } — never throws.
 */

import { getDimAdapter } from "./dimAdapters.js";
import { NOT_IN_DIRECTORY, NO_DEPARTMENT, QUERY_FIELDS, parseQuery } from "./queryLang.js";
import { ensureQueryJoins } from "./queryLangJoins.js";

// Re-export so existing importers (apiClient.js) keep their import path; the
// pure implementation lives in queryLangJoins.js (node-env-safe).
export { ensureQueryJoins };

const OP_PROTO = {
  eq: "OPERATOR_EQ",
  ne: "OPERATOR_NE",
  in: "OPERATOR_IN",
  gt: "OPERATOR_GT",
  gte: "OPERATOR_GTE",
  lt: "OPERATOR_LT",
  lte: "OPERATOR_LTE",
  contains: "OPERATOR_ILIKE",
};

// `*` (the query-language wildcard) → `%` (ClickHouse LIKE wildcard). A
// wildcard-free substring value gets wrapped %value% so `text:foo` and
// `file:foo*` both behave like a human expects.
function toLikePattern(value, { wrapIfNoWildcard }) {
  const s = String(value);
  if (s.includes("*")) return s.replace(/\*/g, "%");
  return wrapIfNoWildcard ? `%${s}%` : s;
}

function fieldRef(def) {
  return { stepAlias: def.step, field: def.field };
}

function leaf(def, op, value) {
  return { pred: { field: fieldRef(def), op: OP_PROTO[op], value } };
}

function not(child) {
  return { notExpr: { child } };
}

/**
 * Compile a parsed AST into { filter, joins, errors, warnings }.
 * `adapterResolver(dim)` returns a dim adapter ({ predicate(values, step) })
 * or null; defaults to the live getDimAdapter registry.
 *
 * `joins` lists the cross-entity hops the filter references ("cluster" and/or
 * "identity"). The caller must add those join steps to the spec root before
 * running — `ensureQueryJoins` / `refineSpecWithQuery` below do that.
 */
export function compileQuery(ast, { adapterResolver = getDimAdapter } = {}) {
  const errors = [];
  const warnings = [];
  const joins = new Set();

  function compile(node) {
    if (!node) return null;
    switch (node.type) {
      case "and": {
        const kids = node.children.map(compile).filter(Boolean);
        // A dropped conjunct broadens the result — tolerable, already warned
        // at the dropped node. 0 → null, 1 → unwrap, n → andExpr.
        if (kids.length === 0) return null;
        if (kids.length === 1) return kids[0];
        return { andExpr: { children: kids } };
      }
      case "or": {
        const kids = node.children.map(compile);
        if (kids.some((k) => !k)) {
          // STRICT: dropping an OR branch would hide rows the user asked for.
          errors.push({ message: "can't compile this OR — one branch isn't queryable yet" });
          return null;
        }
        if (kids.length === 1) return kids[0];
        return { orExpr: { children: kids } };
      }
      case "not": {
        const inner = compile(node.child);
        return inner ? not(inner) : null;
      }
      case "text": {
        // Bare term → free-text search. Matches the prompt body OR the provider
        // tag — the same fields the legacy trace-log Search box ILIKE'd, so the
        // QueryBar replaces it losslessly. HIGH COST (scans enriched_prompt).
        const pattern = toLikePattern(node.value, { wrapIfNoWildcard: true });
        const children = [
          { pred: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENRICHED_PROMPT" }, op: "OPERATOR_ILIKE", value: pattern } },
          { pred: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" }, op: "OPERATOR_ILIKE", value: pattern } },
        ];
        // An email-shaped bare term ("@corp.com", "alice@corp.com") also
        // searches the resolved directory person — the natural gesture for
        // finding Copilot / Compliance-API accounts (attributed by email)
        // without knowing the identity: prefix. Same fields the identity:
        // clause searches; the u→du join is LEFT so the OR keeps prompts with
        // no assignment matching on prompt text alone.
        if (node.sessionIds?.length) {
          children.push({
            pred: {
              field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" },
              op: "OPERATOR_IN",
              value: node.sessionIds,
            },
          });
        }
        if (node.value.includes("@")) {
          joins.add("identity");
          const identity = QUERY_FIELDS.identity;
          for (const f of identity.searchFields) {
            children.push({ pred: { field: { stepAlias: identity.step, field: f }, op: "OPERATOR_ILIKE", value: pattern } });
          }
        }
        return { orExpr: { children } };
      }
      case "pred":
        return compilePred(node);
      case "error":
        return null;
      default:
        return null;
    }
  }

  function compilePred(node) {
    const { def, op, value, key } = node;
    if (!def) return null;
    // Mid-typing: a bare field with no value yet (`file:`) isn't a filter —
    // skip silently so the live query doesn't thrash to 0 rows as you type.
    if (value === "" || (Array.isArray(value) && value.length === 0)) return null;

    if (def.kind === "cluster") {
      // ec.cluster_subtree. Needs the PROMPT_HAS_EVENT_CLUSTER join.
      joins.add("cluster");
      const ref = { stepAlias: def.step, field: def.field };
      const vals = Array.isArray(value) ? value : [value];
      // A non-numeric value is a LABEL — resolveAndCompile rewrites it to id(s)
      // before this runs, so we only reach a label here on the bare-compile path
      // (the QueryBar's live diagnostics). It's valid-pending-resolution, not an
      // error: contribute no filter (the bar shows the chip), no warning.
      if (!vals.every((v) => v !== "" && Number.isFinite(Number(v)))) return null;
      const nums = vals.map(Number);
      const positive = (op === "in" || nums.length > 1)
        ? { pred: { field: ref, op: "OPERATOR_IN", value: nums } }
        : { pred: { field: ref, op: "OPERATOR_EQ", value: nums[0] } };
      // `ne` negates whichever positive shape applies — including a list
      // (`cluster!=1,2` → NOT IN). The old `op==="in" || len>1` arm ran first
      // and silently turned a negated list into a positive IN.
      return op === "ne" ? not(positive) : positive;
    }

    if (def.kind === "endpoint") {
      // Hostname match on the joined ENDPOINT (ep.hostname ILIKE). Needs the
      // PROMPT → ENDPOINT join. No id list → no 1024 cap; one value, a
      // comma-list (OR of ILIKEs), `*` wildcards, and negation all supported.
      joins.add("endpoint_host");
      const ref = { stepAlias: def.step, field: def.field };
      const ilike = (v) => ({ pred: { field: ref, op: "OPERATOR_ILIKE", value: toLikePattern(v, { wrapIfNoWildcard: true }) } });
      const vals = Array.isArray(value) ? value : [value];
      if (op === "ne") return not(ilike(vals[0]));
      if (vals.length > 1) return { orExpr: { children: vals.map(ilike) } };
      return ilike(vals[0]);
    }

    if (def.kind === "group") {
      // The person's department, by the identity join: each value a whole
      // name in any case (no wrapping %), `*` matching within it. Having no
      // group is a clause of its own. The server's LEFT joins leave what
      // matched nothing empty (join_use_nulls=0), so no one assigned is an
      // empty person id, and someone with no department an empty department.
      joins.add("identity");
      const at = (field, proto, v) => ({ pred: { field: { stepAlias: def.step, field }, op: proto, value: v } });
      const person = QUERY_FIELDS.identity.field;
      const one = (v) => {
        const said = String(v).trim().toLowerCase();
        if (said === NOT_IN_DIRECTORY.toLowerCase()) return at(person, "OPERATOR_EQ", "");
        if (said === NO_DEPARTMENT.toLowerCase()) return { andExpr: { children: [at(person, "OPERATOR_NE", ""), at(def.field, "OPERATOR_EQ", "")] } };
        return at(def.field, "OPERATOR_ILIKE", toLikePattern(v, { wrapIfNoWildcard: false }));
      };
      const vals = Array.isArray(value) ? value : [value];
      const positive = vals.length > 1 ? { orExpr: { children: vals.map(one) } } : one(vals[0]);
      return op === "ne" ? not(positive) : positive;
    }

    if (def.kind === "identity") {
      // `identity:` is a person search across the directory user's
      // human-readable fields (display_name / email / upn), so `identity:@corp.com`
      // finds Copilot / Compliance-API accounts whose person is resolved by
      // email. It ALSO matches the directory source_id exactly — the provider's
      // own id, which is a hyphenated UUID for Entra but `00u…` for Okta and a
      // 21-digit number for Google, so we cannot gate the exact match on UUID
      // shape. Every value is matched both ways and ORed: a human field never
      // equals an id and an id never substring-matches a name/email/upn, so the
      // two are disjoint (no false matches) and a pasted or round-tripped id
      // pins the person on every provider. Needs the PROMPT_HAS_USER_ASSIGNMENTS
      // → USER_ASSIGNMENT_HAS_DIRECTORY_USER join.
      joins.add("identity");
      // OR of ILIKE %v% across every human-readable identity field, per value.
      const searchLeaves = (v) =>
        def.searchFields.map((f) => ({
          pred: {
            field: { stepAlias: def.step, field: f },
            op: "OPERATOR_ILIKE",
            value: toLikePattern(v, { wrapIfNoWildcard: true }),
          },
        }));
      const sourceId = (proto, v) => ({
        pred: { field: { stepAlias: def.step, field: def.field }, op: proto, value: v },
      });
      // OR the given expressions, flattening a nested orExpr and dropping any
      // null; a lone child stays bare (no single-element orExpr).
      const anyOf = (...exprs) => {
        const children = exprs.flatMap((e) => (e?.orExpr ? e.orExpr.children : e ? [e] : []));
        return children.length === 1 ? children[0] : { orExpr: { children } };
      };
      const search = (vals) => anyOf(...vals.flatMap(searchLeaves));

      // A wildcard/contains value can't be an exact id — person search only.
      if (op === "contains") return search([value]);
      if (op === "in") {
        const vals = Array.isArray(value) ? value : [value];
        return anyOf(sourceId("OPERATOR_IN", vals), search(vals));
      }
      if (op === "ne") return not(anyOf(sourceId("OPERATOR_EQ", value), search([value])));
      return anyOf(sourceId("OPERATOR_EQ", value), search([value]));
    }

    if (def.kind === "synthetic") {
      const adapter = adapterResolver?.(def.dim);
      const values = Array.isArray(value) ? value : [value];
      const frag = adapter?.predicate?.(values, def.step) ?? null;
      if (!frag) {
        warnings.push({ message: `couldn't build predicate for ${key}` });
        return null;
      }
      return op === "ne" ? not(frag) : frag;
    }

    if (def.kind === "array") {
      // Array(String): element match (IN) or element ILIKE (contains).
      if (op === "contains") {
        return leaf(def, "contains", toLikePattern(value, { wrapIfNoWildcard: true }));
      }
      const values = Array.isArray(value) ? value : [value];
      const inLeaf = { pred: { field: fieldRef(def), op: "OPERATOR_IN", value: values } };
      return op === "ne" ? not(inLeaf) : inLeaf;
    }

    if (def.kind === "text") {
      // Substring search regardless of eq/contains.
      return {
        pred: {
          field: fieldRef(def),
          op: "OPERATOR_ILIKE",
          value: toLikePattern(value, { wrapIfNoWildcard: true }),
        },
      };
    }

    if (def.kind === "int") {
      if (op === "in") {
        const nums = value.map(Number);
        if (nums.some((n) => !Number.isFinite(n))) {
          warnings.push({ message: `${key} expects numbers` });
          return null;
        }
        return { pred: { field: fieldRef(def), op: "OPERATOR_IN", value: nums } };
      }
      const num = Number(value);
      if (!Number.isFinite(num)) {
        warnings.push({ message: `${key} expects a number, got "${value}"` });
        return null;
      }
      return leaf(def, op, num);
    }

    // string / enum
    if (op === "in") return { pred: { field: fieldRef(def), op: "OPERATOR_IN", value } };
    if (op === "contains") {
      return leaf(def, "contains", toLikePattern(value, { wrapIfNoWildcard: true }));
    }
    const norm = def.normalize ? def.normalize(value) : value;
    return leaf(def, op, norm);
  }

  const filter = compile(ast);
  return { filter, joins: [...joins], errors, warnings };
}

/**
 * AND two FilterExprs into one (the refinement composition). Flattens a
 * top-level andExpr so scope + query don't nest needlessly. Either may be
 * null/undefined.
 */
export function composeFilters(a, b) {
  const parts = [];
  for (const f of [a, b]) {
    if (!f) continue;
    if (f.andExpr?.children) parts.push(...f.andExpr.children);
    else parts.push(f);
  }
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0];
  return { andExpr: { children: parts } };
}

// ── Applying a COMPILED query ({filter, joins}) to a spec ───────────────
//
// Split from the string-based wrappers below so a caller that refines several
// specs from one query (getOverviewActivityBundle: agent spec + token collapse)
// can compile ONCE and apply the result to each, instead of re-parsing +
// re-compiling the same string per spec.

/**
 * Attach the compiled query's joins to the PROMPT step and AND its filter onto
 * the spec's existing (scope-derived) filter. Returns the (possibly new) spec;
 * an empty compiled filter returns `spec` unchanged.
 */
export function applyQueryToSpec(spec, compiled) {
  const { filter, joins } = compiled;
  if (!filter) return spec;
  const root = ensureQueryJoins(spec.root, joins);
  const newFilter = composeFilters(spec.filter, filter);
  const next = { ...spec, root };
  if (newFilter) next.filter = newFilter;
  return next;
}

/**
 * Fold the compiled query into a PROMPT→TOKEN *collapse* source root (the token
 * / model diagrams). The query is just more scope: it rides the PROMPT source
 * step's `stepFilter` (the collapse-edge validator rejects a top-level filter
 * on the source — same place scope predicates sit), with cross-entity joins on
 * that step. `identity` (u→du) can't ride the collapse source, so it fails
 * closed (`ok:false`) and the caller blanks the token layer rather than show
 * unfiltered totals. Returns { root, ok, applied }; never mutates the input
 * root. The collapse root is alias `p`.
 */
export function applyQueryToCollapseRoot(root, compiled) {
  const { filter, joins } = compiled;
  if (!filter) return { root, ok: true, applied: false };
  if (joins.includes("identity")) return { root, ok: false, applied: false };
  const base = structuredClone(root);
  const withJoins = joins.length ? ensureQueryJoins(base, joins) : base; // adds cluster `ec`
  withJoins.stepFilter = [...(withJoins.stepFilter || []), filter];
  return { root: withJoins, ok: true, applied: true };
}

// ── String-based wrappers (single-spec surfaces) ────────────────────────

/**
 * Parse + compile `queryText`, then attach joins + AND the filter onto `spec`.
 * Returns { spec, errors, warnings, joins }. Empty / all-error → spec unchanged.
 */
export function refineSpecWithQuery(spec, queryText, opts = {}) {
  const { ast } = parseQuery(queryText || "");
  const compiled = compileQuery(ast, opts);
  return { spec: applyQueryToSpec(spec, compiled), errors: compiled.errors, warnings: compiled.warnings, joins: compiled.joins };
}

/** String-based wrapper over applyQueryToCollapseRoot. */
export function refineCollapseRootWithQuery(root, queryText, opts = {}) {
  const { ast } = parseQuery(queryText || "");
  return applyQueryToCollapseRoot(root, compileQuery(ast, opts));
}
